import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { BookOpen, Award, ExternalLink, Download, Share2, CheckCircle2, Lock, PlayCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { User } from "@supabase/supabase-js";
import { Helmet } from "react-helmet-async";
import { AuthModal } from "@/components/AuthModal";

interface RoadmapProgress {
  id: string;
  roadmap_id: string;
  progress_percentage: number;
  completed_tasks: any;
  updated_at: string;
  roadmaps: {
    content: any;
    blog_posts: {
      title: string;
      slug: string;
      featured_image: string | null;
    };
  };
}

interface Certificate {
  id: string;
  blog_post_title: string;
  learner_name: string;
  issued_at: string;
  certificate_number: string;
}

interface PremiumProgress {
  id: string;
  content_type: "premium_guide" | "digital_resource";
  entity_id: string;
  title: string;
  slug: string | null;
  featured_image: string | null;
  progress_percentage: number;
  last_section: string | null;
  last_accessed_at: string;
  completed_at: string | null;
}

const MyLearning = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [roadmaps, setRoadmaps] = useState<RoadmapProgress[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [premiumProgress, setPremiumProgress] = useState<PremiumProgress[]>([]);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setShowAuthModal(true);
        setLoading(false);
        return;
      }

      setUser(user);
      await fetchUserData(user.id);
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserData(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Auto-refresh when a purchase/subscription is recorded by the webhook
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`my-learning-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "purchases", filter: `user_id=eq.${user.id}` },
        () => fetchUserData(user.id)
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => fetchUserData(user.id)
      )
      .subscribe();

    // Refetch when the tab regains focus (covers webhook arriving while user is on Stripe)
    const onFocus = () => fetchUserData(user.id);
    window.addEventListener("focus", onFocus);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
    };
  }, [user?.id]);

  const fetchUserData = async (userId: string) => {
    try {
      // Fetch roadmap progress
      const { data: progressData, error: progressError } = await supabase
        .from("roadmap_progress")
        .select(`
          id,
          roadmap_id,
          progress_percentage,
          completed_tasks,
          updated_at,
          roadmaps!inner (
            content,
            blog_posts!inner (
              title,
              slug,
              featured_image
            )
          )
        `)
        .eq("user_id", userId)
        .order("updated_at", { ascending: false });

      if (progressError) throw progressError;
      setRoadmaps(progressData || []);

      // Fetch certificates
      const { data: certData, error: certError } = await supabase
        .from("certificates")
        .select("*")
        .eq("user_id", userId)
        .order("issued_at", { ascending: false });

      if (certError) throw certError;
      setCertificates(certData || []);

      // Fetch premium content progress
      const { data: premiumData } = await (supabase as any)
        .from("premium_content_progress")
        .select("*")
        .eq("user_id", userId)
        .order("last_accessed_at", { ascending: false });
      setPremiumProgress(premiumData || []);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getNextDay = (completedTasks: Record<string, boolean>) => {
    for (let day = 1; day <= 28; day++) {
      const dayKey = `day-${day}`;
      if (!completedTasks[dayKey]) {
        return day;
      }
    }
    return 28;
  };

  const inProgressRoadmaps = roadmaps.filter(r => r.progress_percentage < 100);
  const completedRoadmaps = roadmaps.filter(r => r.progress_percentage === 100);
  const inProgressPremium = premiumProgress.filter(p => p.progress_percentage < 100);
  const completedPremium = premiumProgress.filter(p => p.progress_percentage >= 100);

  const totalItems = roadmaps.length + premiumProgress.length;
  const totalCompleted = completedRoadmaps.length + completedPremium.length;
  const avgProgress = totalItems
    ? Math.round(
        ([...roadmaps.map(r => r.progress_percentage), ...premiumProgress.map(p => p.progress_percentage)]
          .reduce((a, b) => a + b, 0)) / totalItems
      )
    : 0;

  const premiumLink = (p: PremiumProgress) => {
    if (p.content_type === "digital_resource") return `/resource/${p.entity_id}/view`;
    const hash = p.last_section ? `#${p.last_section}` : "#section-roadmap";
    return p.slug ? `/blog/${p.slug}${hash}` : `/`;
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-background">
          <div className="container mx-auto px-4 py-12">
            <Skeleton className="h-12 w-64 mb-4" />
            <Skeleton className="h-6 w-96 mb-8" />
            <div className="space-y-6">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Helmet>
          <title>My Learning - Sign In Required | RunoBlog</title>
          <meta name="description" content="Track your learning progress and view your certificates" />
        </Helmet>
        <Navigation />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Card className="max-w-md w-full mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-primary" />
                Sign In to View Your Learning
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Sign in to track your roadmap progress and view your earned certificates.
              </p>
              <Button onClick={() => setShowAuthModal(true)} className="w-full">
                Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
        <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Learning Dashboard | RunoBlog</title>
        <meta name="description" content="Track your learning progress across all roadmaps and view your earned certificates" />
      </Helmet>
      <Navigation />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12">
          {/* Header */}
          <div className="mb-12 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-4xl font-bold mb-4 flex items-center gap-3">
                <BookOpen className="h-8 w-8 text-primary" />
                My Learning
              </h1>
              <p className="text-muted-foreground text-lg">
                Track your progress across all learning roadmaps
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate("/account")}>
              My Account →
            </Button>
          </div>

          {/* My Progress summary */}
          {totalItems > 0 && (
            <div className="grid gap-4 sm:grid-cols-3 mb-10">
              <Card><CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Items in My Learning</p>
                <p className="text-3xl font-bold">{totalItems}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-3xl font-bold">{totalCompleted}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Avg progress</p>
                <p className="text-3xl font-bold">{avgProgress}%</p>
                <Progress value={avgProgress} className="h-2 mt-2" />
              </CardContent></Card>
            </div>
          )}

          {/* Empty State */}
          {totalItems === 0 ? (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="pt-12 pb-12 text-center">
                <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-2xl font-semibold mb-2">Nothing here yet</h2>
                <p className="text-muted-foreground mb-6">
                  Unlock a Premium Guide, start a 28-day roadmap, or open a digital resource — they all appear here.
                </p>
                <Button onClick={() => navigate("/")}>
                  Browse Articles
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-12">
              {/* Continue Learning — Premium */}
              {inProgressPremium.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                    <Lock className="h-6 w-6 text-primary" />
                    Continue Learning ({inProgressPremium.length})
                  </h2>
                  <div className="grid gap-6 md:grid-cols-2">
                    {inProgressPremium.map((p) => (
                      <Card key={p.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                        <CardContent className="p-0">
                          <div className="flex gap-4 p-6">
                            <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-muted">
                              {p.featured_image ? (
                                <img src={p.featured_image} alt={p.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Lock className="h-8 w-8 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <h3 className="font-semibold text-lg line-clamp-2">{p.title}</h3>
                                <Badge variant="secondary" className="flex-shrink-0 text-xs">
                                  {p.content_type === "digital_resource" ? "Resource" : "Premium"}
                                </Badge>
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-muted-foreground">Progress</span>
                                    <span className="font-medium">{p.progress_percentage}%</span>
                                  </div>
                                  <Progress value={p.progress_percentage} className="h-2" />
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  Last activity: {formatDistanceToNow(new Date(p.last_accessed_at), { addSuffix: true })}
                                </p>
                                <Button size="sm" onClick={() => navigate(premiumLink(p))} className="w-full">
                                  <PlayCircle className="mr-2 h-4 w-4" />
                                  Continue Learning
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Premium */}
              {completedPremium.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                    🎉 Completed Premium ({completedPremium.length})
                  </h2>
                  <div className="grid gap-6 md:grid-cols-2">
                    {completedPremium.map((p) => (
                      <Card key={p.id} className="border-primary/20">
                        <CardContent className="p-6 flex items-start gap-4">
                          <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold mb-2 line-clamp-2">{p.title}</h3>
                            <Button size="sm" variant="outline" onClick={() => navigate(premiumLink(p))}>
                              Review
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* In Progress Roadmaps */}
              {inProgressRoadmaps.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                    📊 In Progress ({inProgressRoadmaps.length})
                  </h2>
                  <div className="grid gap-6 md:grid-cols-2">
                    {inProgressRoadmaps.map((roadmap) => {
                      const nextDay = getNextDay(roadmap.completed_tasks);
                      const blogPost = roadmap.roadmaps.blog_posts;

                      return (
                        <Card key={roadmap.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                          <CardContent className="p-0">
                            <div className="flex gap-4 p-6">
                              {/* Thumbnail */}
                              <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-muted">
                                {blogPost.featured_image ? (
                                  <img 
                                    src={blogPost.featured_image} 
                                    alt={blogPost.title}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <BookOpen className="h-8 w-8 text-muted-foreground" />
                                  </div>
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-lg mb-2 truncate">
                                  {blogPost.title}
                                </h3>
                                
                                <div className="space-y-3">
                                  {/* Progress Bar */}
                                  <div>
                                    <div className="flex justify-between text-sm mb-1">
                                      <span className="text-muted-foreground">Progress</span>
                                      <span className="font-medium">{roadmap.progress_percentage}%</span>
                                    </div>
                                    <Progress value={roadmap.progress_percentage} className="h-2" />
                                  </div>

                                  {/* Last Activity */}
                                  <p className="text-xs text-muted-foreground">
                                    Last activity: {formatDistanceToNow(new Date(roadmap.updated_at), { addSuffix: true })}
                                  </p>

                                  {/* Continue Button */}
                                  <Button
                                    size="sm"
                                    onClick={() => navigate(`/blog/${blogPost.slug}/roadmap/day/${nextDay}`)}
                                    className="w-full"
                                  >
                                    Continue Day {nextDay}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Completed Roadmaps */}
              {completedRoadmaps.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                    🏆 Completed ({completedRoadmaps.length})
                  </h2>
                  <div className="grid gap-6 md:grid-cols-2">
                    {completedRoadmaps.map((roadmap) => {
                      const blogPost = roadmap.roadmaps.blog_posts;

                      return (
                        <Card key={roadmap.id} className="overflow-hidden hover:shadow-lg transition-shadow border-primary/20">
                          <CardContent className="p-0">
                            <div className="flex gap-4 p-6">
                              {/* Thumbnail */}
                              <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-muted relative">
                                {blogPost.featured_image ? (
                                  <img 
                                    src={blogPost.featured_image} 
                                    alt={blogPost.title}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <BookOpen className="h-8 w-8 text-muted-foreground" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                  <CheckCircle2 className="h-8 w-8 text-primary" />
                                </div>
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <h3 className="font-semibold text-lg truncate">
                                    {blogPost.title}
                                  </h3>
                                  <Badge variant="default" className="flex-shrink-0">
                                    100%
                                  </Badge>
                                </div>
                                
                                <div className="space-y-3">
                                  {/* Progress Bar */}
                                  <Progress value={100} className="h-2" />

                                  {/* Action Buttons */}
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => navigate(`/blog/${blogPost.slug}/roadmap`)}
                                      className="flex-1"
                                    >
                                      Review Roadmap
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Certificates Section */}
              {certificates.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                    <Award className="h-6 w-6 text-primary" />
                    My Certificates ({certificates.length})
                  </h2>
                  <div className="grid gap-4">
                    {certificates.map((cert) => (
                      <Card key={cert.id}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg mb-1">{cert.blog_post_title}</h3>
                              <p className="text-sm text-muted-foreground mb-2">
                                Completed {formatDistanceToNow(new Date(cert.issued_at), { addSuffix: true })}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Certificate ID: {cert.certificate_number}
                              </p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/verify/${cert.certificate_number}`)}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MyLearning;
